import moment from "moment";

export const FOLLOWUP_STATUS = {
  PENDING: "Pending",
  RESCHEDULED: "Rescheduled",
  SCHEDULED: "Appointment Scheduled",
  CLOSED: "Closed",
};

// One source of truth for follow-up status presentation, so the table chips,
// the calendar dots and the day sidebar can never drift apart.
export const FOLLOWUP_STATUS_META = {
  [FOLLOWUP_STATUS.PENDING]: {
    label: "Pending",
    color: "#b45309",
    bg: "#fff4e5",
    dot: "#f59e0b",
  },
  [FOLLOWUP_STATUS.RESCHEDULED]: {
    label: "Rescheduled",
    color: "#6d28d9",
    bg: "#f3f0ff",
    dot: "#8b5cf6",
  },
  [FOLLOWUP_STATUS.SCHEDULED]: {
    label: "Scheduled",
    color: "#1565c0",
    bg: "#e3f2fd",
    dot: "#1976d2",
  },
  [FOLLOWUP_STATUS.CLOSED]: {
    label: "Closed",
    color: "#0a8f54",
    bg: "#e7f7ee",
    dot: "#0a8f54",
  },
};

const FALLBACK_META = {
  label: "Unknown",
  color: "#6b7280",
  bg: "#f3f4f6",
  dot: "#9ca3af",
};

// Display order for dots, sidebar sections and the legend.
export const FOLLOWUP_STATUS_ORDER = [
  FOLLOWUP_STATUS.PENDING,
  FOLLOWUP_STATUS.RESCHEDULED,
  FOLLOWUP_STATUS.SCHEDULED,
  FOLLOWUP_STATUS.CLOSED,
];

export const statusMeta = (status) => FOLLOWUP_STATUS_META[status] || FALLBACK_META;

// A follow-up nobody has acted on yet; a past day holding one of these is overdue.
export const needsAction = (fu) =>
  !!fu &&
  (fu.status === FOLLOWUP_STATUS.PENDING ||
    fu.status === FOLLOWUP_STATUS.RESCHEDULED);

/**
 * The calendar day a follow-up belongs to. `followUpDate` is stored as a
 * date-only value, so the literal date part is used instead of letting the
 * browser timezone shift an ISO string onto the previous/next day.
 */
export const followUpDay = (value) => {
  if (!value) return null;
  if (typeof value === "string") {
    const dateOnly = moment(value.slice(0, 10), "YYYY-MM-DD", true);
    if (dateOnly.isValid()) return dateOnly;
  }
  const parsed = moment(value);
  return parsed.isValid() ? parsed : null;
};

export const dayKey = (day) => (day ? day.format("YYYY-MM-DD") : "");

// { "2025-11-11": [followUp, ...] } for O(1) lookups while painting the grid.
export const groupByDay = (followUps = []) =>
  followUps.reduce((acc, fu) => {
    const day = followUpDay(fu.followUpDate);
    if (!day) return acc;
    const key = dayKey(day);
    (acc[key] = acc[key] || []).push(fu);
    return acc;
  }, {});

export const sortByStatus = (followUps = []) =>
  [...followUps].sort(
    (a, b) =>
      FOLLOWUP_STATUS_ORDER.indexOf(a.status) -
      FOLLOWUP_STATUS_ORDER.indexOf(b.status)
  );

export const patientNameOf = (fu) =>
  fu && fu.Patient
    ? `${fu.Patient.firstName || ""} ${fu.Patient.lastName || ""}`.trim() || "N/A"
    : "N/A";

export const mobileOf = (fu) =>
  fu && fu.Patient && fu.Patient.mobile
    ? fu.Patient.mobile.replace(/^\+91/, "")
    : "N/A";
